import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("preet@neevadvisors.com");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email.trim(), password);
      nav("/");
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-navy text-white p-16 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white text-navy flex items-center justify-center font-bold">N</div>
            <div>
              <div className="font-semibold text-lg tracking-tight">Neev OS</div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/60">Internal Work System</div>
            </div>
          </div>
        </div>
        <div className="space-y-6 max-w-md">
          <h1 className="text-4xl font-semibold leading-tight tracking-tight">
            One source of truth for everything we ship.
          </h1>
          <p className="text-white/70 text-base leading-relaxed">
            Track tasks, oversee projects, manage retainers — all in one place.
            Built for Neev Advisors, a SEBI Registered Research Analyst firm.
          </p>
        </div>
        <div className="text-xs text-white/40 tracking-wide">© Neev Advisors</div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-6"
          data-testid="login-form"
        >
          <div className="space-y-1">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Sign in</h2>
            <p className="text-sm text-gray-500">Use your Neev Advisors email.</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="w-full bg-white border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="w-full bg-white border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
              />
            </div>
          </div>

          {err && (
            <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-btn"
            className="w-full bg-navy hover:bg-navy-800 disabled:opacity-60 text-white px-4 py-2.5 rounded-md font-medium transition-colors inline-flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Sign in
          </button>

          <div className="text-[11px] text-gray-400 leading-relaxed pt-2 border-t border-gray-100">
            <span className="font-medium text-gray-500">Seeded accounts</span><br />
            preet@neevadvisors.com · krishna@neevadvisors.com · rahul@neevadvisors.com
          </div>
        </form>
      </div>
    </div>
  );
}
